import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, RotateCcw } from "lucide-react";
import { GATES } from "@/data/temple";
import { useJourney, getWeeks, gateProgress } from "@/lib/journey";
import { slugify } from "@/lib/intro";
import { Ring } from "@/components/temple/Ring";
import { Reveal } from "@/components/temple/Reveal";
import { ease } from "@/lib/scroll";
import ReminderOptIn from "@/components/temple/ReminderOptIn";
import Certificate from "@/components/temple/Certificate";
import IntentionJournal from "@/components/temple/IntentionJournal";
import { useBase } from "@/lib/appMode";
import { useAuth } from "@/lib/auth";

const pad = (n) => String(n).padStart(2, "0");

const AppInvite = () => {
  const { user } = useAuth();
  if (!user?.initiate) return null;
  return (
    <Link to="/app" data-testid="journey-app-invite" className="group mt-14 flex items-center gap-5 border border-[#D9B47A]/25 bg-[#0D0908] p-6 hover:border-[#D9B47A]/60 transition-colors">
      <img src="/gates-icon-192.png" alt="" className="w-14 h-14 rounded-2xl shrink-0" />
      <div className="min-w-0">
        <p className="font-display text-xl">Get the 13 Gates to Ecstasy app</p>
        <p className="mt-1 text-sm text-[#A8998C]">Your gates, journals and moon practices on your home screen, with its own icon and an optional passcode.</p>
      </div>
      <ArrowUpRight className="ml-auto w-5 h-5 shrink-0 text-[#D9B47A] transition-transform duration-500 group-hover:rotate-45" />
    </Link>
  );
};

const GateRow = ({ g, i, entry }) => {
  const base = useBase();
  const done = gateProgress(entry);
  const weeks = getWeeks(i);
  const state = done === 3 ? "Mastered" : done > 0 ? "In practice" : "Unopened";
  return (
    <Reveal y={20}>
      <Link
        to={`${base}/gates/${slugify(g.name)}`}
        data-testid={`journey-gate-${i + 1}`}
        className="group relative grid grid-cols-[auto_1fr_auto] items-center gap-5 sm:gap-8 py-6 border-b border-[#D9B47A]/15 hover:bg-[#120C0A] transition-colors duration-500 px-2"
      >
        <span className={`relative z-10 w-11 h-11 rounded-full border flex items-center justify-center font-tt-mono text-[12px] transition-colors duration-500 ${done === 3 ? "bg-[#C2414F] border-[#C2414F] text-[#F2E8DF]" : done ? "border-[#C2414F] text-[#F2E8DF] bg-[#0A0706]" : "border-[#D9B47A]/40 text-[#D9B47A] bg-[#0A0706]"}`}>
          {pad(i + 1)}
        </span>
        <div className="min-w-0">
          <p className="font-display text-2xl sm:text-3xl group-hover:text-[#D9B47A] transition-colors">{g.name}</p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {[0, 1, 2].map((k) => (
              <span key={k} title={weeks[k]?.title} className={`h-1 w-10 sm:w-16 rounded-full ${entry?.weeks?.[k] ? "bg-[#C2414F]" : "bg-[#D9B47A]/20"}`} />
            ))}
            <span className="ml-2 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">{state}</span>
          </div>
          {entry?.note && <p className="mt-3 text-sm text-[#A8998C] italic line-clamp-1">“{entry.note}”</p>}
        </div>
        <ArrowUpRight className="w-5 h-5 text-[#8F8176] group-hover:text-[#D9B47A] group-hover:rotate-45 transition-[color,transform] duration-500" />
      </Link>
    </Reveal>
  );
};

export default function Journey() {
  const { data, reset } = useJourney();
  const base = useBase();
  const [confirm, setConfirm] = useState(false);
  const total = GATES.reduce((n, _, i) => n + gateProgress(data[i]), 0);
  const mastered = GATES.filter((_, i) => gateProgress(data[i]) === 3).length;
  const nextIdx = GATES.findIndex((_, i) => gateProgress(data[i]) < 3);
  const next = nextIdx >= 0 ? GATES[nextIdx] : null;

  return (
    <main data-testid="journey-page" className={`max-w-[1440px] mx-auto px-5 sm:px-10 ${base ? "pt-10" : "pt-32 sm:pt-40"} pb-24`}>
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <p className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">Your Practice Journal</p>
            <h1 className="font-display text-[clamp(3rem,7vw,6.5rem)] leading-[0.95] mt-6">
              {["The", "journey"].map((w, k) => (
                <span key={w} className="block overflow-hidden pb-[0.06em]">
                  <motion.span className={`block ${k ? "italic text-[#D9B47A]" : ""}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.2, delay: 0.1 + k * 0.12, ease }}>
                    {w}
                  </motion.span>
                </span>
              ))}
            </h1>
            <div className="mt-12 flex items-center gap-8">
              <Ring value={total / 39} size={168} stroke={3} testId="journey-total-ring">
                <div>
                  <p data-testid="journey-percent" className="font-display text-5xl">{Math.round((total / 39) * 100)}<span className="text-2xl text-[#8F8176]">%</span></p>
                  <p className="font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#A8998C]">of the path</p>
                </div>
              </Ring>
              <div className="space-y-4">
                <div>
                  <p data-testid="journey-mastered" className="font-display text-4xl text-[#D9B47A]">{mastered}<span className="text-xl text-[#8F8176]">/13</span></p>
                  <p className="text-sm text-[#A8998C]">gates mastered</p>
                </div>
                <div>
                  <p data-testid="journey-weeks" className="font-display text-4xl text-[#D9B47A]">{total}<span className="text-xl text-[#8F8176]">/39</span></p>
                  <p className="text-sm text-[#A8998C]">weeks of practice</p>
                </div>
              </div>
            </div>
            {next ? (
              <Link to={`${base}/gates/${slugify(next.name)}`} data-testid="journey-continue" className="group mt-12 inline-flex items-center gap-3 rounded-full bg-[#D9B47A] text-[#0A0706] px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors">
                {total ? "Continue" : "Begin"} with Gate {pad(nextIdx + 1)} · {next.name}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
              </Link>
            ) : (
              <p data-testid="journey-complete" className="mt-12 font-display italic text-3xl text-[#D9B47A]">Live ecstatically forever. Blessed Be.</p>
            )}
            <p className="mt-8 text-sm text-[#A8998C] max-w-sm">Your journal is private and saved only in this browser. Nothing is sent to the temple.</p>
            {total > 0 && (
              <button data-testid="journey-reset" onClick={() => (confirm ? (reset(), setConfirm(false)) : setConfirm(true))} className="mt-4 inline-flex items-center gap-2 text-sm text-[#A8998C] hover:text-[#E8838E] transition-colors">
                <RotateCcw className="w-4 h-4" /> {confirm ? "Tap again to clear your journal" : "Begin again"}
              </button>
            )}
          </div>
        </div>
        <div className="lg:col-span-7">
          {mastered === 13 && <div className="mb-14 -mt-14"><Certificate data={data} /></div>}
          <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#A8998C] mb-4">From the root to the waking life</p>
          <div className="border-t border-[#D9B47A]/15">
            {GATES.map((g, i) => <GateRow key={g.name} g={g} i={i} entry={data[i]} />)}
          </div>
          <div className="mt-14">
            <IntentionJournal />
          </div>
          {!base && (
            <div className="mt-14">
              <ReminderOptIn />
            </div>
          )}
          {!base && <AppInvite />}
        </div>
      </div>
    </main>
  );
}
